import { useEffect } from "react";
import { Box, Typography, TextField, Button, IconButton, Grid, FormControl, InputLabel, Select, MenuItem, Paper, Divider } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import { CreateStepperRaffleProps } from "../../../../domain/CreateStepperRaffleProps";
import { AWARD_TYPES_ICONS } from "../../../../constants/icons";

export const AwardsStep = ({ resource, setResource, setDisableNext, awards, setAwards }: CreateStepperRaffleProps) => {

  useEffect(() => {
    setResource((prevResource) => ({
      ...prevResource,
      awards: awards,
    }));

    const isFormValid = awards.every((award) => {
      return (
        award.name.trim() !== "" &&
        award.images.length > 0
      );
    });

    if (setDisableNext !== undefined) {
      setDisableNext(!isFormValid || awards.length === 0);
    }

  }, [awards, setResource, setDisableNext]);


  useEffect(() => {
    setResource((prevResource) => ({
      ...prevResource,
      awards: awards,
    }));
  }, [awards, setResource]);

  const handleAddReward = () => {
    if (awards.length >= 2) return;
    // Obtener el primer tipo de premio disponible como valor por defecto
    const defaultType = AWARD_TYPES_ICONS.length > 0 ? AWARD_TYPES_ICONS[0].id : "car";
    setAwards([...awards, { name: "", features: ["-"], images: [], order: awards.length, type: defaultType }]);
  };

  const handleNameChange = (index: number, value: string) => {
    const updatedAwards = [...awards];
    updatedAwards[index].name = value;
    setAwards(updatedAwards);
  };

  const handleAwardTypeChange = (index: number, value: string) => {
    const updatedAwards = [...awards];
    updatedAwards[index].type = value;
    setAwards(updatedAwards);
  };


  const handleRemoveImage = (awardIndex: number, imageIndex: number) => {
    const updatedAwards = [...awards];
    updatedAwards[awardIndex].images.splice(imageIndex, 1);
    setAwards(updatedAwards);
  };

  const handleDeleteReward = (awardIndex: number) => {
    const updatedAwards = [...awards];
    updatedAwards.splice(awardIndex, 1);
    setAwards(updatedAwards);
  };

  const handleImageChange = (index: number, files: FileList | null) => {
    if (!files) return;
    const imagesToAdd = Array.from(files).slice(0, 3);
    const updatedAwards = [...awards];
    updatedAwards[index].images.push(...imagesToAdd);
    setAwards(updatedAwards);
  };

  const imagePreviews = (awardIndex: number) => {
    return awards[awardIndex].images.map((image, imageIndex) => (
        <Grid item xs={12} sm={6} md={4} key={`image-preview-${imageIndex}`}>
          <Paper
            elevation={0}
            sx={{
              p: 1,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              position: 'relative',
            }}
          >
            <Box
              sx={{
                position: 'relative',
                width: '100%',
                paddingTop: '75%', // Aspect ratio 4:3
                overflow: 'hidden',
                borderRadius: 1,
              }}
            >
              <img 
                src={URL.createObjectURL(image)} 
                alt={`Preview ${imageIndex + 1}`}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
            </Box>
            <Box display="flex" justifyContent="center" mt={1}>
              <IconButton 
                onClick={() => handleRemoveImage(awardIndex, imageIndex)}
                size="small"
                color="error"
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          </Paper>
        </Grid>
    ));
  };


  return (
    <Box p={3}>
      <Grid container spacing={3}>
        {awards.map((award, awardIndex) => (
          <Grid item xs={12} key={`award-${awardIndex}`}>
            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
              {/* Header del Premio */}
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h6" fontWeight="600">
                  Premio {awardIndex + 1}
                </Typography>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => handleDeleteReward(awardIndex)}
                >
                  Eliminar
                </Button>
              </Box>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={3}>
                {/* Nombre y Tipo del Premio */}
                <Grid item xs={12} md={6}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        label="Nombre del Premio"
                        variant="outlined"
                        fullWidth
                        required
                        value={award.name}
                        inputProps={{ maxLength: 24 }}
                        onChange={(e) => handleNameChange(awardIndex, e.target.value)}
                        helperText="Máximo 24 caracteres"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl variant="outlined" fullWidth>
                        <InputLabel>Tipo de premio</InputLabel>
                        <Select
                          required
                          label="Tipo de premio"
                          value={award.type}
                          onChange={(e) => handleAwardTypeChange(awardIndex, e.target.value)}
                        >
                          {AWARD_TYPES_ICONS.map((element) => (
                            <MenuItem key={element.id} value={element.id}>
                              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                {element.icon}
                                <Typography>{element.name}</Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Grid>

                {/* Imágenes */}
                <Grid item xs={12} md={6}>
                  <Paper elevation={0} sx={{ p: 2, borderRadius: 2 }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="subtitle1" fontWeight="600">
                        Imágenes del Premio
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {awards[awardIndex].images.length}/3
                      </Typography>
                    </Box>
                    <Grid container spacing={2}>
                      {imagePreviews(awardIndex)}
                      {awards[awardIndex].images.length < 3 && (
                        <Grid item xs={12} sm={6} md={4}>
                          <Button
                            variant="outlined"
                            fullWidth
                            component="label"
                            startIcon={<AddPhotoAlternateIcon />}
                            sx={{
                              py: 3,
                              borderStyle: 'dashed',
                              borderWidth: 2,
                              '&:hover': {
                                borderStyle: 'dashed',
                                borderWidth: 2,
                              }
                            }}
                          >
                            Agregar Imagen
                            <input
                              type="file"
                              accept="image/*"
                              multiple
                              hidden
                              onChange={(e) => handleImageChange(awardIndex, e.target.files)}
                            />
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        ))}

        {/* Botón para agregar premio */}
        {awards.length < 2 && (
          <Grid item xs={12}>
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={handleAddReward}
              sx={{
                py: 2,
                borderStyle: 'dashed',
                borderWidth: 2,
                '&:hover': {
                  borderStyle: 'dashed',
                  borderWidth: 2,
                }
              }}
            >
              + Agregar Premio
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};
